import { useState, useEffect, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';
import { IconEdit, IconTrash, IconUpload, IconCamera } from '../components/Icons';
import './Admin.css';

const defaultForm = { caption: '', location: '', date: '', category: 'academia', tags: '' };
const categories = ['academia', 'conference', 'ecovillage', 'general'];

export default function AdminGallery() {
  const { authFetch } = useAdminAuth();
  const [photos,   setPhotos]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [dragging, setDragging] = useState(false);
  const [form,     setForm]     = useState(defaultForm);
  const [editId,   setEditId]   = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,    setError]    = useState('');
  const [isOtherCategory, setIsOtherCategory] = useState(false);
  const [customCategory,  setCustomCategory]  = useState('');
  const fileInputRef = useRef();

  const loadPhotos = () => {
    setLoading(true);
    fetch('/api/gallery')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      })
      .then(data => { 
        if (data && data.items) {
          setPhotos(data.items);
        } else if (Array.isArray(data)) {
          setPhotos(data);
        } else {
          setPhotos([]);
        }
        setLoading(false); 
      })
      .catch((err) => {
        console.error("Fetch gallery error in admin:", err);
        setPhotos([]);
        setLoading(false);
      });
  };

  useEffect(() => { loadPhotos(); }, []);

  // ── Upload handler ──────────────────────────────────────
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    for (let i = 0; i < files.length; i++) {
        if (!files[i].type.startsWith('image/')) { setError('Please select only image files.'); return; }
    }
    setShowModal(true);
    setEditId(null);
    setForm({ ...defaultForm });
    setIsOtherCategory(false);
    setCustomCategory('');
    // Store file ref on window for upload on save
    window._pendingUploadFiles = Array.from(files);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const max = 1600; // Resize to max 1600px
          if (width > height) { if (width > max) { height *= max / width; width = max; } }
          else { if (height > max) { width *= max / height; height = max; } }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          }, 'image/jpeg', 0.8); // 80% quality
        };
      };
    });
  };

  const handleSaveNew = async () => {
    const rawFiles = window._pendingUploadFiles;
    if (!rawFiles || rawFiles.length === 0) { setError('No files selected.'); return; }
    
    setUploading(true);
    setError('');
    
    try {
      // Compress all files first
      const compressedFiles = await Promise.all(rawFiles.map(file => compressImage(file)));
      
      const fd = new FormData();
      fd.append('caption',  form.caption || '');
      fd.append('location', form.location || '');
      fd.append('date',     form.date || '');
      fd.append('category', isOtherCategory ? customCategory.trim().toLowerCase() : form.category);
      fd.append('tags',     form.tags || '');
      
      compressedFiles.forEach(file => fd.append('photos[]', file));
      
      const res = await authFetch('/api/gallery', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      
      window._pendingUploadFiles = null;
      setShowModal(false);
      loadPhotos();
    } catch (err) {
      console.error("Upload error:", err);
      setError('Upload failed. Please try again.');
    }
    setUploading(false);
  };

  // ── Edit handler ────────────────────────────────────────
  const openEdit = (photo) => {
    setEditId(photo.id);
    setForm({ caption: photo.caption, location: photo.location, date: photo.date, category: photo.category, tags: (photo.tags||[]).join(', ') });
    setIsOtherCategory(false);
    setCustomCategory('');
    window._pendingUploadFiles = null;
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    setUploading(true);
    setError('');
    try {
      const res = await authFetch(`/api/gallery/${editId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          category: isOtherCategory ? customCategory.trim().toLowerCase() : form.category,
          tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) 
        }),
      });
      if (!res.ok) throw new Error('Update failed');
      setShowModal(false);
      loadPhotos();
    } catch {
      setError('Failed to update. Please try again.');
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this photo? This cannot be undone.')) return;
    await authFetch(`/api/gallery/${id}`, { method: 'DELETE' });
    loadPhotos();
  };

  return (
    <AdminLayout title="Gallery Photos" page="gallery">
      <div className="admin-content">
        <div className="admin-page-header">
          <h2>Gallery Photos</h2>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
        </div>

        {error && <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 'var(--radius-sm)', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#b91c1c', fontSize: '0.85rem' }}>{error}</div>}

        {/* Upload zone */}
        <div
          id="gallery-upload-zone"
          className={`admin-upload-zone ${dragging ? 'admin-upload-zone--drag' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          aria-label="Upload photo"
        >
          <div className="admin-upload-zone__icon"><IconUpload size={48} color="var(--navy)" /></div>
          <div className="admin-upload-zone__title">Drag & drop a photo here, or click to browse</div>
          <div className="admin-upload-zone__hint">JPEG, PNG, WEBP · Max 15MB</div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          multiple
          onChange={e => handleFiles(e.target.files)}
        />

        {/* Photo grid */}
        {loading ? (
          <div className="admin-loading">Loading photos…</div>
        ) : (
          <div className="admin-photo-grid">
            {photos.map(photo => (
              <div key={photo.id} className="admin-photo-card">
                <img src={photo.images && photo.images.length > 0 ? photo.images[0] : (photo.thumb || photo.src)} alt={photo.caption} className="admin-photo-card__img" />
                <div className="admin-photo-card__body">
                  <div className="admin-photo-card__caption" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: photo.caption ? 'inherit' : 'var(--text-light)', fontStyle: photo.caption ? 'normal' : 'italic' }}>
                    {photo.caption || '(no caption)'}
                  </div>
                  <div className="admin-photo-card__meta">
                    {photo.date || 'Year?'} {photo.location ? `· ${photo.location}` : ''}
                  </div>
                  <div className="admin-photo-card__actions">
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(photo)} title="Edit"><IconEdit size={14} /></button>
                    <button className="btn btn-danger btn-sm"  onClick={() => handleDelete(photo.id)} title="Delete"><IconTrash size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload / Edit modal */}
      {showModal && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal">
            <h3>{editId ? 'Edit Photo Details' : 'Add Photo Details'}</h3>
            <div className="admin-form">
              <div className="admin-field">
                <label>Story / Caption</label>
                {window._pendingUploadFiles && !editId && (
                   <p style={{ fontSize: '0.85rem', color: 'var(--navy)', marginBottom: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                     <IconCamera size={18} /> {window._pendingUploadFiles.length} photo(s) selected
                   </p>
                )}
                <textarea value={form.caption} onChange={e => setForm(f => ({...f, caption: e.target.value}))} placeholder="Write the story here..." rows={4} style={{ width: '100%', padding: '0.6rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontFamily: 'inherit' }} />
              </div>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label>Location</label>
                  <input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="City, Country" />
                </div>
                <div className="admin-field">
                  <label>Date / Year</label>
                  <input value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} placeholder="2024" />
                </div>
              </div>
              <div className="admin-field">
                <label>Category</label>
                <div className="admin-form-row" style={{ gap: '0.5rem', marginBottom: 0 }}>
                  <select 
                    value={isOtherCategory ? 'other' : form.category} 
                    onChange={e => {
                      if (e.target.value === 'other') {
                        setIsOtherCategory(true);
                      } else {
                        setIsOtherCategory(false);
                        setForm(f => ({...f, category: e.target.value}));
                      }
                    }}
                    style={{ flex: 1 }}
                  >
                    {/* Combine defaults with existing categories from data */}
                    {Array.from(new Set([...categories, ...photos.map(p => p.category)])).sort().map(c => (
                      <option key={c} value={c} style={{ color: 'white' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                    <option value="other" style={{ color: '#94a3b8', fontStyle: 'italic' }}>Other (Enter New)</option>
                  </select>
                  {isOtherCategory && (
                    <input 
                      value={customCategory} 
                      onChange={e => setCustomCategory(e.target.value)} 
                      placeholder="New category..." 
                      style={{ flex: 1 }}
                      autoFocus
                    />
                  )}
                </div>
              </div>
              <div className="admin-field">
                <label>Tags (comma-separated)</label>
                <input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} placeholder="conference, keynote, ugm" />
              </div>
              {error && <p style={{ color: '#dc2626', fontSize: '0.82rem' }}>{error}</p>}
            </div>
            <div className="admin-modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => { setShowModal(false); window._pendingUploadFiles = null; }}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={editId ? handleSaveEdit : handleSaveNew} disabled={uploading}>
                {uploading ? (editId ? 'Saving…' : 'Uploading…') : (editId ? 'Save Changes' : 'Post Photos')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
